import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '@pc/api/client';
import type { operations } from '@pc/api/schema';
import { Absent, Id, Person, type PersonRecord } from '../ui';
import { ACCOUNT_PAGE_LIMIT, accountDirectoryKey } from './OwnerPicker';

/**
 * A subject or job actor, named from the accounts this client already has —
 * never from a lookup of its own.
 *
 * Backend MR !65 deliberately did not add a `Person` for a job's subject the
 * way it did for its actor, and said why: a subject is a member, `GET
 * /api/accounts` already carries an `owner` for every account, and at
 * roughly 49 accounts a client can join one from a single cached call rather
 * than the API paying a batched lookup per page. `OwnerPicker` already loads
 * that call for its own suggestions — see `accountDirectoryKey` — so
 * `useAccountDirectory` below rides the same cache entry instead of asking
 * again.
 *
 * **The rule that matters, and the one a later edit is most likely to get
 * wrong:** a subject's snowflake being absent from `useAccountDirectory()`'s
 * map means *this client has not loaded that account* — not that the
 * accounts directory holds no record of them. `GET /api/accounts` pages 200
 * at a time and this map is built only from whatever pages this particular
 * screen happens to have fetched so far, which for a picker nobody has typed
 * into yet may be one page, or none. "Not in the map" is a fact about this
 * browser tab's cache, not a fact about the person — the account may hold a
 * full record one page further along, or on a page nothing on this screen
 * ever had reason to request.
 *
 * That is why `KnownPerson`'s fallback below is deliberately inert: the
 * plain snowflake, linked exactly the way every caller already rendered it
 * before this component existed — never a `Person`-style directory note
 * ("name not recorded", "no directory record", "directory unavailable"),
 * never an `<Absent>` standing in for the person, and nothing else that
 * would read as a lookup having run and come back empty. Rendering any of
 * those would be reporting a directory answer this component never asked
 * the directory for.
 */

type ListResponse = operations['listAccounts']['responses'][200]['content']['application/json'];
type ListQuery = NonNullable<operations['listAccounts']['parameters']['query']>;

/**
 * The shape the contract puts on every Discord id it will accept —
 * `/accounts` declares this same `^[1-9][0-9]{16,19}$` for its own path
 * parameter. An account's own `ownerDiscordID` is guaranteed to be one, but
 * the id this component falls back to may have come off a job or audit row
 * with no such promise attached, and an id of another shape names no account
 * `/accounts` can look up — a link that lands on a refusal is worse than the
 * plain text it replaced.
 */
const DISCORD_ID_PATTERN = /^[1-9][0-9]{16,19}$/;

/**
 * Every account page this client has loaded, joined into `ownerDiscordID ->
 * owner`.
 *
 * Shares `accountDirectoryKey` with `OwnerPicker`, so on a screen that
 * already mounts one (`JobsBrowser`, the audit search form) this issues no
 * request of its own — TanStack Query dedupes on the key, and both hooks
 * describe the identical request. Left enabled unconditionally, rather than
 * gated on a picker being present, so a screen with no picker still gets a
 * first page to join against instead of silently joining nothing.
 *
 * Does not page itself forward: `OwnerPicker` already walks this same cache
 * up to its own page cap the moment it mounts (see its `pageCount` effect),
 * and duplicating that walk here would race the same key from two callers
 * for no more coverage. Without a picker on screen, callers get whatever the
 * first page holds — partial, never wrong, and exactly what "this client has
 * not loaded that account" already accounts for.
 */
export function useAccountDirectory(): Map<string, PersonRecord> {
  const { data } = useInfiniteQuery({
    queryKey: accountDirectoryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<ListResponse> => {
      const { data } = await api.GET('/api/accounts', {
        params: {
          query: {
            limit: ACCOUNT_PAGE_LIMIT,
            ...(pageParam === undefined ? {} : { cursor: pageParam }),
          } satisfies ListQuery,
        },
      });
      return data as ListResponse;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  return useMemo(() => {
    const directory = new Map<string, PersonRecord>();
    for (const page of data?.pages ?? []) {
      for (const record of page.records) {
        if (record.owner) directory.set(record.ownerDiscordID, record.owner);
      }
    }
    return directory;
  }, [data]);
}

/**
 * A Discord id, named when this client's cached accounts happen to include
 * it and left exactly as every caller already rendered it when they do not.
 *
 * `discordID` takes `null` so a caller with a possibly-absent id (the audit
 * trail's `subjectDiscordID`) does not need a branch of its own in front of
 * this one: a null id was never anyone to look up, which is a different,
 * unrelated fact from "not in the map" and keeps the one `<Absent>` this
 * component renders scoped to exactly that case.
 */
export function KnownPerson({
  discordID,
  linkTitle,
}: {
  discordID: string | null;
  linkTitle: string;
}) {
  const directory = useAccountDirectory();
  const owner = discordID === null ? undefined : directory.get(discordID);

  if (owner) return <Person person={owner} compact />;

  // No id at all to look up — an absent field, not a subject this client
  // merely hasn't cached. See the file comment for why those two must not be
  // told the same way.
  if (discordID === null) return <Absent />;

  if (!DISCORD_ID_PATTERN.test(discordID)) return <Id>{discordID}</Id>;

  return (
    <Link to="/accounts" search={{ discordID }} title={linkTitle}>
      <Id>{discordID}</Id>
    </Link>
  );
}
